/**
 * @file AdminMeetingsFilters.tsx
 * @brief Filter bar for platform-wide MeetingIntel — search + company + owner + dates.
 *
 * @details
 * Flush `GridCells` layout matched to Sessions/Users filters (29-07-2026).
 * Owner picker still narrows conceptually by clearing when company changes.
 *
 * @author DES
 * @date 08-07-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026: company / owner became header pills
 *          (`AdminMeetingsHeaderFilters`), the date the page header's circle;
 *          no Clear button. This file's bar is the search.
 */

import { useEffect, useState } from "react";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import { UserPicker } from "@/features/users/components/UserPicker";
import type { AdminMeetingsListParams } from "../schemas";

interface AdminMeetingsFiltersProps {
  value: AdminMeetingsListParams;
  onChange: (next: AdminMeetingsListParams) => void;
}

/** Company and owner as the page header's text pills. */
export function AdminMeetingsHeaderFilters({
  value,
  onChange,
}: AdminMeetingsFiltersProps) {
  return (
    <>
      <CompanyPicker
        variant="pill"
        value={value.company_id ?? null}
        onChange={(companyId) =>
          onChange({
            ...value,
            company_id: companyId ?? undefined,
            owner_id: undefined,
            page: 1,
          })
        }
        placeholder="Any company"
      />
      <UserPicker
        variant="pill"
        value={value.owner_id ?? null}
        onChange={(userId) =>
          onChange({ ...value, owner_id: userId ?? undefined, page: 1 })
        }
        companyId={value.company_id ?? null}
        placeholder="Any user"
      />
    </>
  );
}

/** The search, rendered by the page inside its bottom dock (DES 01-10-2026). */
export function AdminMeetingsFilters({
  value,
  onChange,
}: AdminMeetingsFiltersProps) {
  const [searchInput, setSearchInput] = useState(value.search ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.search ?? "") === debouncedSearch) return;
    onChange({ ...value, search: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    // The Notes screen's search (DES 01-10-2026), rendered by the page
    // inside its bottom PageDock ("dock style ki tarha neechay").
    <ExpandingSearch
      value={searchInput}
      onChange={(next) => setSearchInput(next)}
      placeholder="ID, title, participant, owner, company, overview"
      label="Search meetings"
    />
  );
}
